import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { m } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import OptimizedImage from "@/components/OptimizedImage";
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslatedProducts } from "@/hooks/useTranslatedProducts";
import { type Product } from "@/data/products";

// Product grid image sizes - typically 3 columns on desktop, 2 on tablet, 1 on mobile
const PRODUCT_IMAGE_SIZES = "(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw";

const Products = () => {
  const { t } = useTranslation();
  const { translatedProducts } = useTranslatedProducts();
  const { localePath, currentLang } = useLanguage();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("kategorie") || "all";
  const [activeCategory, setActiveCategory] = useState<string>(initialCategory);

  const categories: { value: Product["category"] | "all"; label: string }[] = [
    { value: "all", label: t("products.all") },
    { value: "vertikalni", label: t("products.vertical") },
    { value: "horizontalni", label: t("products.horizontal") },
    { value: "kompaktory", label: t("products.compactors") },
  ];

  const filteredProducts = activeCategory === "all"
    ? translatedProducts
    : translatedProducts.filter((p) => p.category === activeCategory);

  const getCategoryLabel = (cat: Product["category"]) => {
    if (cat === "vertikalni") return t("products.categoryVertical");
    if (cat === "horizontalni") return t("products.categoryHorizontal");
    return t("products.categoryCompactor");
  };

  // Generate full URL with language prefix
  const getFullUrl = (path: string) => {
    const langPrefix = currentLang === 'cs' ? '' : `/${currentLang}`;
    return `https://ekokompakt.cz${langPrefix}${path}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={`${t("products.badge")} — ${t("products.vertical")} | EkoKompakt`}
        description={t("products.subtitle")}
        canonical={getFullUrl("/produkty")}
        ogImage="https://ekokompakt.cz/og-image.png"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": t("products.badge"),
            "url": getFullUrl("/produkty"),
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "numberOfItems": filteredProducts.length,
            "itemListElement": filteredProducts.map((product, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Product",
                "name": product.name,
                "description": product.shortDescription,
                "url": getFullUrl(`/produkt/${product.slug}`),
                "image": product.image,
                "brand": { "@type": "Brand", "name": "EkoKompakt" },
                "category": getCategoryLabel(product.category)
              }
            }))
          }
        ]}
      />
      <Header />
      <main>
        <section className="pt-28 pb-12 md:pt-36 md:pb-16">
          <div className="container mx-auto px-4 sm:px-6 text-center">
            <m.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="text-sm font-medium text-primary mb-4 block">{t("products.badge")}</span>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                {t("products.title")} <span className="text-gradient">{t("products.titleHighlight")}</span> {t("products.titleEnd")}
              </h1>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{t("products.subtitle")}</p>
            </m.div>
          </div>
        </section>

        <section className="pb-8">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((cat) => (
                <Button key={cat.value} variant={activeCategory === cat.value ? "default" : "outline"} onClick={() => setActiveCategory(cat.value)} className="rounded-full">
                  {cat.label}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {activeCategory === "vertikalni" && (
          <section className="pb-8">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <p className="text-muted-foreground text-center">{t("products.verticalDesc")}</p>
            </div>
          </section>
        )}
        {activeCategory === "horizontalni" && (
          <section className="pb-8">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <p className="text-muted-foreground text-center">{t("products.horizontalDesc")}</p>
            </div>
          </section>
        )}
        {activeCategory === "kompaktory" && (
          <section className="pb-8">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <p className="text-muted-foreground text-center">{t("products.compactorsDesc")}</p>
            </div>
          </section>
        )}

        <section className="py-8 pb-24">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, index) => (
                <div key={product.slug}>
                  <Link
                    to={localePath(`/produkt/${product.slug}`)}
                    aria-label={`${t("products.detail")}: ${product.name}`}
                  >
                    <article className="group rounded-2xl overflow-hidden bg-card border border-border hover:border-primary/30 transition-all duration-300 h-full flex flex-col">
                      <div className="relative h-56 overflow-hidden">
                        <OptimizedImage
                          src={product.image}
                          alt={`${product.name} — ${product.shortDescription}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          priority={index < 6}
                          sizes={PRODUCT_IMAGE_SIZES}
                          width={400}
                          height={224}
                        />
                      </div>
                      <m.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.05 }} className="p-6 flex flex-col flex-1">
                        <span className="text-xs font-medium text-primary mb-2 block">{getCategoryLabel(product.category)}</span>
                        <h2 className="font-heading text-2xl font-semibold mb-3">{product.name}</h2>
                        <p className="text-muted-foreground text-sm mb-4 flex-1">{product.shortDescription}</p>
                        <Button variant="outline" className="w-full group-hover:border-primary group-hover:text-primary">
                          {`${t("products.detail")}: ${product.name}`}
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </m.div>
                    </article>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Products;
